import { FormikHelpers, Formik, Form } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { Card, CardHeader, CardContent, MenuItem } from "@material-ui/core";

import {
  CONFIRMATION_TEXT,
  ApprovalFields
} from "src/components/ApprovalFields";
import { useAdmin, useTenant } from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import FormikCallingHoursFields from "src/components/form/FormikCallingHoursFields";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import SubmitButton from "src/components/form/SubmitButton";
import { ApprovalRequirement } from "src/graphql/generated/globalTypes";
import {
  subaccountEditCampaignDefaults,
  subaccountEditCampaignDefaultsVariables,
  subaccountEditCampaignDefaults_subaccountEditCampaignDefaults_settings
} from "src/graphql/generated/subaccountEditCampaignDefaults";
import { EDIT_SUBACCOUNT_CAMPAIGN_DEFAULTS } from "src/graphql/subaccount";
import {
  areaCodeHelperText,
  callerPhoneInAreaCodeHelperText
} from "src/util/areaCodeHelperText";
import useMutation from "src/util/useMutation";
import { endTime, startTime } from "src/util/yupValidators";

type Values = {
  timezone: string;
  startTime: string;
  endTime: string;
  areaCode: string;
  callerPhoneInAreaCode: string;
  customer_profile_sid: string;
  approved_stir_shaken_sid: string;
  approved_voice_integrity_sid: string;
  approved_branded_calling_sid: string;
  approved_branded_calling_basic_sid: string;
  defaultProvider: string;
  approval_requirement: string;
  voicemail_allowed: boolean;
  approverRole: string;
};

const schemaForCountry = (countryIso: string) =>
  yup.object().shape({
    timezone: yup.string().required("Required"),
    startTime: startTime(),
    endTime: endTime(),
    areaCode: yup.string().required("Required").areaCode(countryIso),
    callerPhoneInAreaCode: yup.string().areaCode(countryIso),
    customer_profile_sid: yup.string(),
    approved_stir_shaken_sid: yup.string(),
    approved_voice_integrity_sid: yup.string(),
    approved_branded_calling_sid: yup.string(),
    approved_branded_calling_basic_sid: yup.string(),
    defaultProvider: yup.string(),
    approval_requirement: yup.string().required(),
    approverRole: yup.string(),
    voicemail_allowed: yup.boolean().required()
  });

function valuesFromGql(
  gql: subaccountEditCampaignDefaults_subaccountEditCampaignDefaults_settings
): Values {
  return {
    areaCode: gql.campaignDefaultAreaCode,
    callerPhoneInAreaCode: gql.campaignCallerPhoneInAreaCode || "",
    ...gql.campaignDefaultCallingHours,
    customer_profile_sid: gql.customer_profile_sid || "",
    approved_stir_shaken_sid: gql.approved_stir_shaken_sid || "",
    approved_voice_integrity_sid: gql.approved_voice_integrity_sid || "",
    approved_branded_calling_sid: gql.approved_branded_calling_sid || "",
    approved_branded_calling_basic_sid:
      gql.approved_branded_calling_basic_sid || "",
    defaultProvider: gql.defaultProvider,
    approval_requirement: gql.approval_requirement,
    voicemail_allowed: gql.voicemail_allowed,
    approverRole: ""
  };
}

export default function CampaignDefaults(): JSX.Element {
  const { subaccount } = useSubaccount()!;
  const tenant = useTenant()!;
  const snackbar = useSnackbar();
  const admin = useAdmin()!;

  const [updateSubaccountCampaignDefaults] = useMutation<
    subaccountEditCampaignDefaults,
    subaccountEditCampaignDefaultsVariables
  >(EDIT_SUBACCOUNT_CAMPAIGN_DEFAULTS, {
    onErrorSnackbarMessage: "Failed to update campaign defaults."
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const { errors, data } = await updateSubaccountCampaignDefaults({
      variables: {
        subaccountId: subaccount.id,
        campaignDefaults: {
          campaignDefaultAreaCode: values.areaCode,
          campaignCallerPhoneInAreaCode: values.callerPhoneInAreaCode,
          campaignDefaultCallingHours: {
            startTime: values.startTime,
            endTime: values.endTime,
            timezone: values.timezone
          },
          customer_profile_sid: values.customer_profile_sid,
          approved_stir_shaken_sid: values.approved_stir_shaken_sid,
          approved_voice_integrity_sid: values.approved_voice_integrity_sid,
          approved_branded_calling_sid: values.approved_branded_calling_sid,
          approved_branded_calling_basic_sid:
            values.approved_branded_calling_basic_sid,
          approval_requirement:
            values.approval_requirement as ApprovalRequirement,
          voicemail_allowed: values.voicemail_allowed,
          defaultProvider: values.defaultProvider
        },
        confirmationTextAtTimeOfUpdate: CONFIRMATION_TEXT,
        approverRole: values.approverRole
      }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Campaign defaults updated.", {
        variant: "info"
      });
      helpers.resetForm({
        values: valuesFromGql(data!.subaccountEditCampaignDefaults.settings)
      });
    }
  }

  return (
    <Card>
      <CardHeader title="Campaign Defaults" />
      <CardContent>
        <Formik
          initialValues={valuesFromGql(subaccount.settings)}
          onSubmit={handleSubmit}
          validationSchema={schemaForCountry(tenant.countryIso)}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <p>
                These settings will be used for all new campaigns. Changing
                these settings <strong>will not</strong> affect existing
                campaigns.
              </p>
              <FormikErrorBanner />
              <FormikTextField
                label="Default Caller ID area code"
                name="areaCode"
                helperText={areaCodeHelperText(tenant.countryIso)}
                disabled={isSubmitting}
                fullWidth
              />
              <FormikTextField
                label="Caller phone-in number area code (optional)"
                name="callerPhoneInAreaCode"
                helperText={callerPhoneInAreaCodeHelperText(tenant.countryIso)}
                disabled={isSubmitting}
                fullWidth
              />
              <FormikCallingHoursFields
                label="Default Calling Hours"
                disabled={isSubmitting}
              />
              {admin.isStaff && (
                <>
                  <FormikTextField
                    name="customer_profile_sid"
                    label="Twilio Customer Profile SID"
                    fullWidth
                  />
                  <FormikTextField
                    name="approved_stir_shaken_sid"
                    label="Approved Trust Product SID for STIR/SHAKEN"
                    fullWidth
                  />
                  <FormikTextField
                    name="approved_voice_integrity_sid"
                    label="Approved Trust Product SID for Voice Integrity Beta"
                    fullWidth
                  />
                  <FormikTextField
                    name="approved_branded_calling_sid"
                    label="Approved Trust Product SID for Enhanced Branded Calling"
                    placeholder="BU.."
                    fullWidth
                  />
                  <FormikTextField
                    name="approved_branded_calling_basic_sid"
                    label="Approved Trust Product SID for Basic Branded Calling"
                    placeholder="BU.."
                    fullWidth
                  />

                  <FormikSelect
                    name="defaultProvider"
                    fullWidth
                    label="Default provider"
                    disabled={isSubmitting}
                  >
                    <MenuItem key="twilio" value="twilio">
                      Twilio (default)
                    </MenuItem>
                    <MenuItem key="bandwidth" value="bandwidth">
                      Bandwidth
                    </MenuItem>
                  </FormikSelect>
                </>
              )}
              <ApprovalFields />
              <SubmitButton
                type="submit"
                disabled={!dirty}
                isSubmitting={isSubmitting}
                submitText="Update"
                loadingText="Updating..."
              />
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
